<template>
  <div class="flex w-full flex-col">
    <BaseMenu
      v-slot="{ open }"
      v-if="items"
      :multi-select="isMultiSelect"
      :items="
        items.map((item) => ({
          ...item,
          id: item.id ?? 'none',
          leadingIcon: isSelected(item.id) ? 'check' : undefined,
          callback: () => handleItemClick(item.id ?? 'none'),
        }))
      "
    >
      <div
        class="bg-surface-container-low rounded-large relative flex w-full flex-col overflow-hidden border-[3px] border-transparent"
        :class="[
          error &&
            'border-error hover:border-on-error-container text-on-surface focus:border-error',
          !error &&
            !open &&
            'border-outline hover:border-on-surface text-on-surface-variant hover:text-on-surface',
          !error && open && 'border-primary!',
        ]"
      >
        <BaseIcon
          v-if="leadingIcon"
          size="24"
          class="text-on-surface-variant absolute top-4 left-3"
          >{{ leadingIcon }}</BaseIcon
        >
        <BaseIcon
          size="24"
          class="text-on-surface-variant absolute top-4 right-3"
          :class="[open && 'rotate-180']"
          >keyboard_arrow_down</BaseIcon
        >
        <div
          class="peer rounded-extra-small text-body-large relative z-0 h-14 w-full pr-12 outline-none"
          :class="[
            !readonly && !disabled && 'cursor-pointer',
            readonly && 'pointer-events-none',
            disabled && 'pointer-events-none',
            error && 'text-on-surface',
            !error && 'text-on-surface-variant hover:text-on-surface focus:text-on-surface',
            leadingIcon && 'pr-4 pl-13 focus:pl-[50px]',
            !leadingIcon && 'px-4',
            label && isLabelFloated(open) && 'pt-6',
            (!label || !isLabelFloated(open)) && 'pt-4',
          ]"
        >
          <slot name="selected-text">
            {{ selectedItemTitle }}
          </slot>
        </div>
        <label
          class="pointer-events-none absolute bg-inherit px-1 transition-all"
          :class="[
            isLabelFloated(open)
              ? 'text-body-small top-1.5 translate-y-0'
              : 'text-body-large top-1/2 -translate-y-1/2',
            error && 'text-error',
            !error && open && 'text-primary',
            !error && !open && 'text-on-surface-variant',
            leadingIcon && !isLabelFloated(open) ? 'left-13' : 'left-3',
          ]"
        >
          {{ label }}
          {{ !hideDecorators ? (required ? '*' : `(${$t('common.optional')})`) : '' }}
        </label>
      </div>
    </BaseMenu>
    <p v-if="error" class="text-error text-body-small mt-1 px-4">
      {{ $t(error, { field: label }) }}
    </p>
  </div>
</template>

<script lang="ts" setup>
  import { computed } from 'vue'
  import BaseIcon from '../icon/BaseIcon.vue'
  import BaseMenu from '../menu/BaseMenu.vue'
  import type { SingleMenuItem } from '../menu/MenuItem.ts'

  interface Props {
    items: (Omit<SingleMenuItem, 'callback' | 'items' | 'id'> & {
      id: string | undefined
      color?: string
    })[]
    error?: string
    leadingIcon?: string
    label?: string
    disabled?: boolean
    readonly?: boolean
    required?: boolean
    manualFocused?: boolean
    hideDecorators?: boolean
    autofocus?: boolean
  }

  const props = defineProps<Props>()

  const model = defineModel<string | string[] | undefined>()
  const isMultiSelect = computed(() => Array.isArray(model.value))
  const hasSelection = computed(() => {
    if (Array.isArray(model.value)) return model.value.length > 0
    return !!model.value
  })
  const isLabelFloated = (open: boolean) => open || hasSelection.value
  const selectedItemTitle = computed(() => {
    if (Array.isArray(model.value)) {
      return model.value.map((item) => props.items.find((i) => i.id === item)?.title).join(', ')
    }
    if (model.value === undefined) {
      return ''
    }
    return props.items.find((item) => item.id === model.value)?.title ?? ''
  })

  const handleItemClick = (id: string | undefined) => {
    if (!id) return
    if (Array.isArray(model.value)) {
      model.value = model.value.includes(id)
        ? model.value.filter((item) => item !== id)
        : [...model.value, id]
    } else {
      model.value = id === 'none' ? undefined : id
    }
  }

  const isSelected = (id: string | undefined) => {
    if (Array.isArray(model.value)) {
      return !!id && model.value.includes(id)
    }
    if (!id) return model.value === undefined
    return model.value === id
  }
</script>
